import { useEffect } from "react";
import { Modal, Form, Input, Checkbox, Select, message } from "antd";
import { WebApiMemberDtoMemberDto } from "../../Repository/eventbookyapi";
import { MemberService } from "../../Services/MemberService";
import { TextService } from "../../Services/TextService";
import { EmailNotificationPreference } from "../../Types/EmailNotificationPreference";
import { GroupType } from "../../Types/GroupType";
import { wantsNoEmail } from "../../Helpers/FamilyMemberHelper";

interface FamilyMemberFormProps {
    open: boolean;
    onClose: () => void;
    onSaved: () => void;
    /** The household's primary member (used when adding). */
    primaryMemberId: number;
    /** The family member being edited; omit to add a new one. */
    member?: WebApiMemberDtoMemberDto;
    /** All company groups (the form filters out the default / "all members" group). */
    groups?: any[];
    disabled?: boolean;
}

const FamilyMemberForm = (props: FamilyMemberFormProps) => {
    const [form] = Form.useForm();
    const GetText = TextService.GetText;
    const isEdit = props.member != null;

    // Same as AddMember: show every real group; the "default" group is pre-checked but can be unchecked.
    // g.isDefaultGroup marks the synthetic "all members" pseudo-group (id -1) — hide only that.
    const realGroups = (props.groups || []).filter((g: any) => g && g.id > 0 && !g.isDefaultGroup);
    const groupOptions = realGroups.map((g: any) => ({ label: g.name, value: g.id }));
    const defaultGroupIds: number[] = realGroups
        .filter((g: any) => g.groupType === GroupType.Default)
        .map((g: any) => g.id);

    useEffect(() => {
        if (!props.open) return;
        form.setFieldsValue({
            firstName: props.member?.firstName ?? "",
            lastName: props.member?.lastName ?? "",
            email: props.member?.email ?? "",
            phoneNumber: props.member?.phoneNumber ?? "",
            noEmail: props.member ? wantsNoEmail(props.member) : false,
            groups: props.member ? (props.member.groups ?? []) : defaultGroupIds,
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.open, props.member, form]);

    const handleOk = async () => {
        try {
            const values = await form.validateFields();
            const payload = {
                firstName: values.firstName,
                lastName: values.lastName,
                email: values.email || "",
                phoneNumber: values.phoneNumber || "",
                emailNotificationPreference: values.noEmail
                    ? EmailNotificationPreference.NoInformationEmails
                    : EmailNotificationPreference.Unspecified,
                groups: values.groups ?? [],
            };

            if (isEdit) {
                await MemberService.UpdateFamilyMember({ memberId: props.member!.memberId!, ...payload });
            } else {
                await MemberService.AddFamilyMember({ primaryMemberId: props.primaryMemberId, ...payload });
            }

            message.success(GetText("Common_SettingsUpdated"));
            props.onSaved();
            props.onClose();
        } catch (e: any) {
            if (e?.errorFields) return; // validation error — Form shows it
            message.error(GetText("EditMember_ErrorSaving") || "Could not save.");
        }
    };

    return (
        <Modal
            title={isEdit
                ? (GetText("FamilyMemberForm_EditTitle") || "Redigera familjemedlem")
                : (GetText("FamilyMemberForm_AddTitle") || "Lägg till familjemedlem")}
            open={props.open}
            onOk={handleOk}
            onCancel={props.onClose}
            okText={GetText("Common_Save") || "Spara"}
            cancelText={GetText("Common_Cancel") || "Avbryt"}
            okButtonProps={{ disabled: props.disabled, "data-testid": "family-member-save" } as any}
            destroyOnClose
        >
            <Form form={form} layout="vertical" disabled={props.disabled}>
                <Form.Item name="firstName" label={GetText("MemberList_FirstNameColumn") || "Förnamn"} rules={[{ required: true }]}>
                    <Input data-testid="family-firstName" />
                </Form.Item>
                <Form.Item name="lastName" label={GetText("MemberList_LastNameColumn") || "Efternamn"}>
                    <Input data-testid="family-lastName" />
                </Form.Item>
                <Form.Item name="email" label={GetText("MemberList_EmailColumn") || "E-post"} rules={[{ type: "email" }]}>
                    <Input data-testid="family-email" />
                </Form.Item>
                <Form.Item name="phoneNumber" label={GetText("MemberList_PhoneColumn") || "Telefon"}>
                    <Input data-testid="family-phone" />
                </Form.Item>
                {groupOptions.length > 0 && (
                    <Form.Item name="groups" label={GetText("MemberList_GroupsColumn") || "Grupper"}>
                        <Select mode="multiple" allowClear options={groupOptions} data-testid="family-groups" />
                    </Form.Item>
                )}
                <Form.Item name="noEmail" valuePropName="checked">
                    <Checkbox>{GetText("MemberList_FamilyEmailOptOut") || "Önskar inte info via e-post"}</Checkbox>
                </Form.Item>
            </Form>
        </Modal>
    );
};

export default FamilyMemberForm;
